import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { supabase } from "../lib/supabase";
import type { TodoStore } from "../lib/store";
import { isTextInput } from "../lib/utils";
import { AddTask } from "./AddTask";
import { CommandPalette } from "./CommandPalette";
import { Icon } from "./Icon";
import { ProjectNav } from "./ProjectNav";
import { TaskList } from "./TaskList";
import { ViewTabs } from "./ViewTabs";

interface TodoAppProps {
  store: TodoStore;
  email: string;
}

export function TodoApp(props: TodoAppProps) {
  let searchInput: HTMLInputElement | undefined;
  let newTaskInput: HTMLInputElement | undefined;
  const [commandsOpen, setCommandsOpen] = createSignal(false);
  const [composerOpen, setComposerOpen] = createSignal(false);
  const [sidebarOpen, setSidebarOpen] = createSignal(false);

  function focusNewTask() {
    setComposerOpen(true);
    queueMicrotask(() => newTaskInput?.focus());
  }

  function onKeyDown(event: KeyboardEvent) {
    const meta = event.metaKey || event.ctrlKey;
    if (isTextInput(event.target)) {
      if (event.key === "Escape") (event.target as HTMLElement).blur();
      return;
    }
    if (meta && event.key.toLowerCase() === "k") {
      event.preventDefault();
      setCommandsOpen(true);
      return;
    }
    if (meta && event.key.toLowerCase() === "z") {
      event.preventDefault();
      void props.store.undoDelete();
      return;
    }
    if (event.key === "/") {
      event.preventDefault();
      searchInput?.focus();
      return;
    }
    if (event.key.toLowerCase() === "n") {
      event.preventDefault();
      focusNewTask();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      props.store.moveSelection(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      props.store.moveSelection(-1);
      return;
    }
    const selected = props.store.selectedId();
    if (!selected) return;
    if (event.key === " " || event.key.toLowerCase() === "e") {
      event.preventDefault();
      if (event.key === " ") {
        const task = props.store.tasks().find((item) => item.id === selected);
        if (task) void props.store.updateTask(selected, { completed: !task.completed });
      } else {
        document.querySelector<HTMLElement>(`[data-edit-task="${selected}"]`)?.click();
      }
    }
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      void props.store.deleteTask(selected);
    }
  }

  onMount(() => window.addEventListener("keydown", onKeyDown));
  onCleanup(() => window.removeEventListener("keydown", onKeyDown));

  return (
    <main class="app-shell">
      <Show when={sidebarOpen()}>
        <button class="sidebar-scrim" aria-label="Close navigation overlay" onClick={() => setSidebarOpen(false)} />
      </Show>
      <aside class="sidebar" classList={{ open: sidebarOpen() }} aria-label="Todo navigation">
        <div class="sidebar-top">
          <div class="brand">
            <span class="brand-mark"><Icon name="check" size={19} /></span>
            <span>Quiet</span>
          </div>
          <button class="small-icon-button sidebar-close" aria-label="Close navigation" onClick={() => setSidebarOpen(false)}>
            <Icon name="close" />
          </button>
        </div>
        <button class="sidebar-add-button" onClick={() => {
          focusNewTask();
          setSidebarOpen(false);
        }}>
          <Icon name="plus" size={18} />
          Add task
        </button>
        <ViewTabs store={props.store} onNavigate={() => setSidebarOpen(false)} />
        <ProjectNav
          projects={props.store.projects}
          activeProject={props.store.projectFilter}
          setProject={(id) => {
            props.store.setProjectFilter(id);
            if (id) props.store.setView("all");
          }}
          addProject={props.store.addProject}
          updateProject={props.store.updateProject}
          deleteProject={props.store.deleteProject}
          onNavigate={() => setSidebarOpen(false)}
        />
        <div class="sidebar-footer">
          <div class="account">
            <span class="account-avatar" aria-hidden="true">{props.email.slice(0, 1).toUpperCase()}</span>
            <span class="account-email">{props.email}</span>
          </div>
          <button class="small-icon-button" aria-label="Sign out" onClick={() => void supabase?.auth.signOut()}>
            <span class="sign-out-label">Sign out</span>
          </button>
        </div>
      </aside>

      <section class="workspace" aria-label="Tasks">
        <header class="mobile-bar">
          <button class="small-icon-button" aria-label="Open navigation" onClick={() => setSidebarOpen(true)}>
            <Icon name="menu" />
          </button>
          <span class="mobile-brand">Quiet</span>
          <button class="mobile-add-button" aria-label="Add task" onClick={focusNewTask}>
            <Icon name="plus" />
          </button>
        </header>
        <div class="workspace-inner">
          <header class="topbar">
            <div class="view-heading">
              <h1>{titleForView(props.store.view(), props.store.projectFilter(), props.store.projects())}</h1>
              <p>{subtitleForView(props.store.view(), props.store.visibleTasks().length)}</p>
            </div>
            <div class="topbar-actions">
              <label class="search">
                <Icon name="search" size={17} />
                <span class="sr-only">Search tasks</span>
                <input
                  ref={searchInput}
                  type="search"
                  aria-label="Search tasks"
                  placeholder="Search tasks"
                  value={props.store.search()}
                  onInput={(event) => props.store.setSearch(event.currentTarget.value)}
                />
                <kbd>/</kbd>
              </label>
              <button class="toolbar-button command-button" onClick={() => setCommandsOpen(true)}>
                <span>Commands</span>
                <kbd>Ctrl K</kbd>
              </button>
              <button
                class="toolbar-button theme-button"
                aria-label={props.store.theme() === "light" ? "Use dark theme" : "Use light theme"}
                onClick={() => props.store.applyTheme(props.store.theme() === "light" ? "dark" : "light")}
              >
                <Icon name={props.store.theme() === "light" ? "moon" : "sun"} />
              </button>
            </div>
          </header>

          <AddTask
            inputRef={(element) => (newTaskInput = element)}
            store={props.store}
            open={composerOpen()}
            setOpen={setComposerOpen}
          />
          <StatusBar store={props.store} />
          <TaskList store={props.store} onAddTask={focusNewTask} />
        </div>
      </section>

      <Show when={props.store.lastDeleted()}>
        <div class="undo-toast" role="status">
          Task deleted.
          <button onClick={() => void props.store.undoDelete()}>Undo</button>
        </div>
      </Show>

      <CommandPalette
        open={commandsOpen()}
        onClose={() => setCommandsOpen(false)}
        onNewTask={focusNewTask}
        store={props.store}
      />
    </main>
  );
}

function titleForView(view: string, projectId: string | null, projects: Array<{ id: string; name: string }>) {
  if (projectId) return projects.find((project) => project.id === projectId)?.name ?? "Project";
  if (view === "today") return "Today";
  if (view === "upcoming") return "Upcoming";
  if (view === "completed") return "Completed";
  return "All tasks";
}

function subtitleForView(view: string, count: number) {
  if (view === "today") {
    return new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric" }).format(new Date());
  }
  if (count === 0) return "Nothing waiting here";
  return `${count} ${count === 1 ? "task" : "tasks"}`;
}

function StatusBar(props: { store: TodoStore }) {
  return (
    <div class="status-bar" aria-live="polite">
      <Show when={props.store.loading()}>
        <span>Loading local tasks...</span>
      </Show>
      <Show when={!props.store.syncStatus().online}>
        <span class="state offline">Offline. Changes are saved locally.</span>
      </Show>
      <Show when={props.store.syncStatus().syncing}>
        <span class="state syncing">Syncing {props.store.syncStatus().pending} change(s)...</span>
      </Show>
      <Show when={props.store.syncStatus().error}>
        <span class="state error">Sync needs attention: {props.store.syncStatus().error}</span>
        <button class="text-button" onClick={() => void props.store.syncNow(true)}>Retry</button>
      </Show>
      <Show when={props.store.syncStatus().nextRetryAt && props.store.syncStatus().error}>
        <span>Next retry {formatRetryTime(props.store.syncStatus().nextRetryAt)}</span>
      </Show>
    </div>
  );
}

function formatRetryTime(value: string | null) {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(value));
}
